import React, { MouseEventHandler } from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import { isTelavoxBrand } from 'flow/utils/brand-utils';
import { RELEASE_NOTES } from 'flow/utils/app-utils';
import { getDateDescription } from 'flow/utils';
import Page from './Setting/Page';
import Divider from './ui-components/Divider';
import { getBrandKey } from 'flow/brands/brands';

const DateHeader = ({ releaseDate }: { releaseDate: string }) => {
    const { i18next } = useAppContext();
    const formattedDate = getDateDescription(releaseDate, i18next);

    return <Divider>{formattedDate}</Divider>;
};

const VersionHeader = ({ versionId }: { versionId: string }) => (
    <p className="mb-2 text-sm text-gray-800">{`Version ${versionId}`}</p>
);

const NewsWrapper = ({ children }: { children: React.ReactNode }) => (
    <div className="my-6 flex flex-col gap-y-2 text-xs text-gray-500">
        {children}
    </div>
);

interface Props {
    onBack?: MouseEventHandler;
}

/**
 * To add a new release to the release notes
 *
 * Copy paste the DateHeader and NewsWrapper to get the correct styling
 * just change the releaseDate and versionId so that they match the
 * correct values.
 *
 * The news added to the paragraphs should be small, easy to read and just the
 * essential basic information needed for the user. Keep the language to English
 * for now. No translation needed!
 */
const ReleaseNotes = ({ onBack }: Props) => {
    return (
        <Page header={RELEASE_NOTES} onClickBack={onBack}>
            <DateHeader releaseDate={'2026-10-26'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.6.2'} />
                <p>
                    {
                        'FIXED: Turning off Make calls no longer stops incoming calls. Make calls is on again after this update, so turn it off in Settings if you had it off'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-10-13'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.6.1'} />
                <p>{'FIXED: Cache issue'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-10-12'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.6.0'} />
                <p>
                    {
                        'NEW: Add keywords to PBX services and find them by keyword in the PBX tab'
                    }
                </p>
                <p>
                    {
                        'FIXED: Sign out in the desktop app when using social login'
                    }
                </p>
                <p>{'FIXED: General bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-10-05'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.5.4'} />
                <p>{'FIXED: Issue with tel and callto links'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-10-02'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.5.3'} />
                <p>{'FIXED: General bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-10-01'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.5.2'} />
                <p>
                    {'NEW: Background noise filter for your microphone (beta)'}
                </p>
                <p>{"NEW: See colleagues' status and pictures in meetings"}</p>
                <p>{'NEW: Formatted AI call summaries'}</p>
                <p>{'FIXED: Calling and video improvements'}</p>
                <p>{'FIXED: Better keyboard and screen reader support'}</p>
                <p>{'FIXED: General bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-09-17'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.5.1'} />
                <p>
                    {
                        'FIXED: The web app no longer reloads repeatedly when opened from admin'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-09-14'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.5.0'} />
                <p>
                    {
                        'NEW: Meeting hosts can allow or deny guests waiting in the lobby'
                    }
                </p>
                <p>
                    {
                        'FIXED: Call history now shows the person you actually spoke to after an attended transfer'
                    }
                </p>
                <p>{'FIXED: Chat responsiveness improved'}</p>
                <p>{'FIXED: General bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-08-31'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.4.2'} />
                <p>{'FIXED: Minor bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-08-17'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.4.1'} />
                <p>{'FIXED: Minor bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-08-03'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.4.0'} />
                <p>
                    {
                        'FIXED: Caller ID now updates correctly after an attended call transfer'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-07-20'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.3.5'} />
                <p>{'FIXED: Minor bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-07-08'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.3.4'} />
                <p>{'FIXED: Video bugs fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-07-01'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.3.3'} />
                <p>{'FIXED: Video bugs fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-06-24'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.3.2'} />
                <p>
                    {
                        'FIXED: Auto Update stopped app from starting for Mac Users'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-06-17'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.3.1'} />
                <p>{'FIXED: General bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-06-08'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.3.0'} />
                <p>{'FIXED: Bugs related to chat for video calls'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-06-01'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.2.11'} />
                <p>{'FIXED: General bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-05-25'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.2.10'} />
                <p>
                    {
                        'NEW: Desktop app automatically remembers your session for all login methods'
                    }
                </p>
                <p>{'FIXED: General bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-05-12'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.2.9'} />
                <p>{'FIXED: Issue with phrases cached improperly'}</p>
            </NewsWrapper>
            <NewsWrapper>
                <VersionHeader versionId={'2.2.8'} />
                <p>{'FIXED: Call history V2 bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-04-30'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.2.7'} />
                <p>{'NEW: Enhanced call history'}</p>
                <p>{'FIXED: General bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-04-17'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.2.6'} />
                <p>{'FIXED: General bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-04-13'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.2.5'} />
                <p>{'FIXED: App stuck on login when started while offline'}</p>
                <p>
                    {
                        'FIXED: Language defaulting to english on settings page when accessed from mobile'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-03-30'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.2.4'} />
                <p>
                    {
                        'FIXED: Removed confusing voicemail help text when creating sound for a queue'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-03-16'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.2.3'} />
                <p>{'FIXED: General bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-03-09'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.2.2'} />
                <p>{'FIXED: Persist language preference'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-03-02'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.2.1'} />
                <p>
                    {
                        'NEW: Universal Mac desktop app supporting Intel and Apple Silicon'
                    }
                </p>
                <p>
                    {
                        'FIXED: Creating new external contact crashed contact page'
                    }
                </p>
                <p>{'FIXED: Avatar/profile pic not vertically aligned'}</p>
                <p>
                    {
                        'FIXED: Own extension is not filtered in dialpad extensions'
                    }
                </p>
                <p>{'FIXED: Added info text for queue login status'}</p>
                <p>
                    {
                        'FIXED: Language selection dropdown is not working in electron apps'
                    }
                </p>
                <p>{'FIXED: Download voicemail is stuck in a loading state'}</p>
                <p>{'FIXED: General bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-02-16'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.2.0'} />
                <p>
                    {
                        'NEW: Editable keyboard shortcuts, edit it in settings page'
                    }
                </p>
                <p>{'FIXED: Improved manage token page for dark mode'}</p>
                <p>
                    {
                        'FIXED: Warning when shared voicemail is full or about to get full'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-02-02'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.1.10'} />
                <p>{'FIXED: Issues with displaying images sent in a chat'}</p>
                <p>{'FIXED: General bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-01-26'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.1.9'} />
                <p>
                    {
                        'FIXED: Bug where unavailable users were shown as available.'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-01-21'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.1.8'} />
                <p>{'FIXED: Callhistory scroll not loading older entries'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-01-21'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.1.7'} />
                <p>
                    {
                        'FIXED: Sending a chat message unintentionally when opening post creator if there is text in the input field'
                    }
                </p>
                <p>
                    {
                        'FIXED: Loading issues when listing images, files or posts in the chat settings.'
                    }
                </p>
                <p>{'FIXED: General bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2026-01-07'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.1.6'} />
                <p>
                    {
                        'FIXED: Loading state of transcription in call history for ai coworker'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-12-08'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.1.5'} />
                <p>
                    {
                        'NEW: Background images/blurred background are available for conference calls over video'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-11-24'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.1.4'} />
                <p>
                    {
                        'NEW: Reply to messages and add reactions directly in chat'
                    }
                </p>
                <p>{'FIXED: Improved logotypes for dark mode'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-11-18'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.1.3'} />
                <p>{'FIXED: Various bug fixes'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-11-10'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.1.2'} />
                <p>
                    {
                        'FIXED: PBX sound creation now correctly creates global sounds instead of defaulting to private'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-10-27'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.1.1'} />
                <p>
                    {
                        'FIXED: Manual triggered calendar event for IVR were not visible in the graphical view'
                    }
                </p>
                <p>
                    {
                        'FIXED: Issue where incoming call details were not displaying correctly on profiles.'
                    }
                </p>
                <p>
                    {
                        'FIXED: Users unable to access all queues under "Queuing System & Live Chat Login".'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-10-13'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.1.0'} />
                <p>
                    {
                        'FIXED: Resolved image Attachment Preview not loading in chat'
                    }
                </p>
                <p>
                    {
                        'NEW: Dark mode available for all users! Go to Settings > General > Theme to enable it.'
                    }
                </p>
                <p>
                    {
                        'NEW: Admin can now make changes in settings on behalf of other users. Do this by clicking on the menu of a user in the contact list and choose "Edit user settings".'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-10-10'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.0.11'} />
                <p>{'FIXED: issue where login would give error'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-09-29'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.0.10'} />
                <p>
                    {
                        'FIXED: Resolved an issue where the zoom keyboard shortcut was not functioning correctly'
                    }
                </p>
                <p>
                    {'FIXED: Layout issue with long attachment names in chat'}
                </p>
                <p>{'FIXED: Overriding behaviour for shortcuts in Excel'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-09-23'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.0.9'} />
                <p>{'FIXED: System update for CTI'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-09-22'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.0.8'} />
                <p>
                    {
                        'FIXED: Performance issue in contact page related to the calendar'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-09-15'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.0.7'} />
                <p>
                    {
                        'FIXED: Visual calendar bug where the opening hours would not display correctly'
                    }
                </p>
                <p>
                    {
                        'FIXED: Call recording to a call not showing up if the user has bad internet connection'
                    }
                </p>
                <p>
                    {
                        'FIXED: Not being able to send sms from the create new chat page'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-09-01'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.0.6'} />
                <p>
                    {
                        'FIXED: Resolved issue where the send button was not displayed correctly when creating a new chat'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-08-27'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.0.5'} />
                <p>
                    {
                        'FIXED: Resolved an issue where auto-update wouldn’t download the installer correctly'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-08-25'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.0.4'} />
                <p>
                    {
                        'FIXED: Resolved an issue where some settings pages would remain stuck in a loading state'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-08-04'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.0.3'} />
                <p>
                    {
                        'NEW: Displaying if calls are handled on other device in call control'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-07-21'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.0.2'} />
                <p>
                    {
                        'FIXED: Show information if user does not have voicemail access'
                    }
                </p>
                <p>{'FIXED: Old chat messages were not loading properly'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-07-02'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.0.1'} />
                <p>
                    {
                        'FIXED: Answer and hangup buttons are shown on hover in minimized call'
                    }
                </p>
                <p>
                    {
                        'FIXED: Application icon is now showing properly for the .msi installation'
                    }
                </p>
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-06-23'} />
            <NewsWrapper>
                <VersionHeader versionId={'2.0.0'} />
                <p>{'FIXED: Admin can clear calendar with only work hours'}</p>
                <p>
                    {
                        'FIXED: You can now create a new chat even if a colleague or group chat shares the same name as an existing conversation'
                    }
                </p>
                <p>
                    {
                        'FIXED: Bug where that prevented updating custom greetings in live chat profiles'
                    }
                </p>
                <p>{'FIXED: Chat shortcuts cut of in narrow view'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-06-09'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.210.0'} />
                <p>
                    {'NEW: Design for video conference lobby has been updated'}
                </p>
                <p>
                    {
                        'FIXED: Bug where the page could break when creating a new profile and changing the shown number'
                    }
                </p>
                <p>
                    {
                        'FIXED: Issue where the PBX/Queue profile would not visually update when changed'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-06-03'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.209.0'} />
                <p>
                    {
                        'FIXED: Error was thrown when searching for empty string when creating a new profile'
                    }
                </p>
                <p>{'FIXED: Download links in general settings'}</p>
                <p>
                    {
                        'FIXED: Not being able to close some dialogs on smaller screens'
                    }
                </p>
                <p>
                    {
                        'FIXED: Zoom level reset when resizing the window in the desktop app'
                    }
                </p>
                <p>{'FIXED: Call history loading indefinitely'}</p>
                <p>
                    {
                        'FIXED: Profiles in settings not hidden when no editing rights'
                    }
                </p>
                <p>{'FIXED: Possibility to unarchive chats on small screen'}</p>
                <p>
                    {'FIXED: External calendar sync was not working properly'}
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-05-12'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.208.0'} />
                <p>{'FIXED: Show name of attachment in chat list'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-04-29'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.207.2'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-04-28'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.207.1'} />
                <p>{'FIXED: Crash that occurred on creating contact'}</p>
                <p>
                    {
                        'FIXED: Crash when trying to access context menu for a queue call'
                    }
                </p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-04-28'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.207.0'} />
                <p>{'FIXED: Deleting calendar events'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-04-17'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.206.3'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-04-16'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.206.2'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-04-15'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.206.1'} />
                <p>{'FIXED: Guests unable to join video conference'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-04-14'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.206.0'} />
                <p>{'NEW: Setting for recording personal calls'}</p>
                <p>{'NEW: Call history detail page when selecting calls'}</p>
                <p>
                    {
                        'FIXED: Selecting a mention in a chat does not navigate to contacts'
                    }
                </p>
                <p>{'FIXED: Context menu for chat members stays in view'}</p>
                <p>{'FIXED: Various other bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-03-20'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.205.1'} />
                <p>{'FIXED: Pbx queue buttons redirected to blank pages'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-03-17'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.205.0'} />
                <p>{'FIXED: Context menus close when choosing an option'}</p>
                <p>
                    {
                        'FIXED: Correct phone number populated on contact created via queue call'
                    }
                </p>
                <p>
                    {'FIXED X-button to clear input in chat search bar is back'}
                </p>
                <p>{'FIXED: Various other bug fixes and improvements'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-03-04'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.204.1'} />
                <p>{'FIXED: App colors are correct again!'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-03-03'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.204.0'} />
                <p>{'FIXED: Dialpad in minimized mode is now usable'}</p>
                <p>
                    {
                        'FIXED: Ringtones with terminal with Always forward-setting'
                    }
                </p>
                <p>{'FIXED: Call contact information on ongoing call wrong'}</p>
                <p>
                    {'FIXED: Possibility to add space in contact information'}
                </p>
                <p>
                    {
                        'FIXED: Navigation from likes and comments on posts in chats works'
                    }
                </p>
                <p>{'FIXED: No limit on incoming queue calls in Queue view'}</p>
            </NewsWrapper>
            <DateHeader releaseDate={'2025-02-18'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.203.1'} />
                <p>{'FIXED: Increase performance of video call'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2025-02-17'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.203.0'} />
                <p>{'NEW: Create a meeting directly from meeting lobby'}</p>
                <p>
                    {
                        'FIXED: Better default date and time when using profile picker'
                    }
                </p>
                <p>
                    {
                        'FIXED: Contacts picture will save the same way as other info'
                    }
                </p>
                <p>{'FIXED: Performance related to messages'}</p>
                <p>
                    {
                        'FIXED: Able to change colleague profiles with Profile activation permission'
                    }
                </p>
                <p>
                    {
                        'FIXED: Able to start a call while having an ongoing mobile call'
                    }
                </p>
                <p>
                    {'FIXED: Shared voicemail shows who marked call as handled'}
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2025-01-28'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.202.1'} />
                <p>{'FIXED: Sorting issue when editing chat room'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2025-01-23'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.202.0'} />
                <p>{'FIXED: Issue with clear calendar button in Work hours'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2025-01-16'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.201.2'} />
                <p>
                    {
                        'FIXED: Missing sound in video conference with external users'
                    }
                </p>
                <p>{'FIXED: Visual bug in video conference view'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2025-01-14'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.201.1'} />
                <p>{'FIXED: Call ends after application freezes'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2025-01-13'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.201.0'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-12-20'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.200.2'} />
                <p>{'FIXED: High CPU usage after computer sleep'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-12-11'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.200.1'} />
                <p>{'FIXED: Various bug fixes.'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-12-05'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.200.0'} />
                <p>
                    {
                        'FIXED: Multiple notifications were showing for calls answered on other terminal'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-11-18'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.199.0'} />
                <p>{'FIXED: Various bug fixes'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-11-04'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.198.1'} />
                <p>{'FIXED: Various bug fixes'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-11-04'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.198.0'} />
                <p>{'FIXED: Various bug fixes'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-10-22'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.197.0'} />
                <p>{'FIXED:Various bug fixes'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-10-16'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.196.4'} />
                <p>{'FIXED: Fixed a bug related to chat list'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-10-16'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.196.3'} />
                <p>{'FIXED: More bug fixes'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-10-11'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.196.2'} />
                <p>{'FIXED: Various bug fixes'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-10-08'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.196.1'} />
                <p>
                    {
                        'FIXED: Fixed issue with call modal not being fully visible'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-10-07'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.196.0'} />
                <p>{'FIXED: Various bug fixes'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-10-01'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.195.1'} />
                <p>{'FIXED: Various bug fixes'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-09-23'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.195.0'} />
                <p>{'NEW: Improved search functionality in colleagues list'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-09-12'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.194.0'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-08-28'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.193.2'} />
                <p>{'FIXED: Various bug fixes'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-08-26'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.193.1'} />
                <p>{'FIXED: Various bug fixes'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-08-26'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.193.0'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-08-12'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.192.0'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-07-29'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.191.0'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-07-15'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.190.0'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-07-01'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.189.0'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-06-17'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.188.0'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-06-03'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.187.0'} />
                <p>{'FIXED: Even MORE bug fixes and improvements! 🐛🔨'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-05-06'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.186.0'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-04-22'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.185.0'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-04-08'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.184.0'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-04-03'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.183.1'} />
                <p>{'FIXED: Login issue with SSO'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-03-25'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.183.0'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-03-11'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.182.0'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
                <p>{'FIXED: High CPU usage in Desktop app'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-02-26'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.181.0'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-02-12'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.180.0'} />
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-01-29'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.179.0'} />
                <p>
                    {
                        'NEW: The 🥳 emoji is finally available from the emoji picker. Now you can get the party started!'
                    }
                </p>
                <p>{'FIXED: Took care of some tricky bugs! 🐛'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2024-01-15'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.178.0'} />
                <p>{'NEW: Minimize application to system tray for Windows'}</p>
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-12-15'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.177.2'} />
                <p>
                    {
                        'NEW: Instantly return to Central Switch from any Contact Card with just one click, smooth navigation at your fingertips'
                    }
                </p>
                <p>
                    {
                        'FIXED: You can now call speed dial numbers using call method "Call with mobile"'
                    }
                </p>
                <p>
                    {
                        'Fixed: Resolved login loop and refresh issues for improved cross-browser compatibility'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-12-05'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.176.0'} />
                <p>{'FIXED: General bug fixes'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-11-20'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.175.0'} />
                <p>
                    {
                        'FIXED: Uninstalled eSIM should be possible to install again'
                    }
                </p>
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-11-07'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.174.0'} />
                <p>{'NEW: Added support for special characters in SMS'}</p>
                <p>{'FIXED: sending SMS to formatted numbers now works'}</p>
                <p>
                    {'FIXED: Removed broken image icon when no logo is present'}
                </p>
                <p>{'FIXED: Various bug fixes and improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-10-24'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.173.1'} />
                <p>
                    {
                        "FIXED: External users without an account couldn't access the external video conference"
                    }
                </p>
                <p>
                    {
                        'FIXED: When uninstalling the app on windows we remove all files'
                    }
                </p>
                <p>
                    {
                        'FIXED: Mobile data usage did not update unless you logged out and in again'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-10-09'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.172.0'} />
                <p>
                    {
                        'FIXED: UI bug in members list when creating a new chatroom'
                    }
                </p>
                <p>{'NEW: Added info for queue settings under profiles'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-09-26'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.171.0'} />
                <p>{'FIXED: Various bugfixes and overall improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-09-18'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.170.2'} />
                <p>{'FIXED: Bugfix in chat functionality'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-09-14'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.170.0'} />
                <p>
                    {
                        'FIXED: When a user is added or removed to/from a chat, all other members of that chat will be updated of the member change'
                    }
                </p>
                <p>
                    {'FIXED: Fixed scrollbar behavior in communication canvas'}
                </p>
                <p>{'NEW: Added info box under dial pad'}</p>
                <p>
                    {
                        'FIXED: Navigate to contact-button now opens the contact card section'
                    }
                </p>
                <p>{'FIXED: Various bugfixes and overall improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-08-21'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.169.0'} />
                <p>{'FIXED: Include mobile calls in a contact call history'}</p>
                <p>
                    {
                        'FIXED: Call icon sometimes not being present on contact cards'
                    }
                </p>
                <p>{'FIXED: Jumpy scrolling behaviour in colleagues tab'}</p>
                <p>
                    {
                        'FIXED: Disconnect tone was sometimes not played when calling unreachable number'
                    }
                </p>
                <p>
                    {
                        'FIXED: Queue login status could end up incorrect after logging in/out manually'
                    }
                </p>
                <p>{'FIXED: Scrollbar pushing out content on some browsers'}</p>
                <p>{'FIXED: Loading spinner not spinning'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-06-20'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.168.0'} />
                <p>{'FIXED: Various bugfixes and overall improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-06-07'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.167.0'} />
                <p>
                    {
                        'FIXED: New contact card Schedule component now shows the future events for current day'
                    }
                </p>
                <p>{'FIXED: Various bugfixes and overall improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-05-24'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.166.1'} />

                <p>{'NEW: Added italian language support'}</p>
                <p>
                    {
                        'FIXED: Disconnect tones were not always played on outgoing calls'
                    }
                </p>
                <p>
                    {
                        'FIXED: Calling through other IP phone option did not work'
                    }
                </p>
                <p>{'FIXED: Various bugfixes and overall improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-05-17'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.165.2'} />

                <p>
                    {
                        'FIXED: Some non-email contact fields were formated as an email'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-05-16'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.165.1'} />

                <p>
                    {
                        'FIXED: Pagination for list of calls in a queue when there is a large amount of calls'
                    }
                </p>
                <p>
                    {
                        'FIXED: Case where a transfer dialog would show the wrong contact info'
                    }
                </p>
                <p>
                    {
                        'FIXED: Correct call behaviour when clicking a number on the contact card whilst in a call'
                    }
                </p>
                <p>
                    {
                        'FIXED: Crash that could occur when clicking the mail button on the contact card'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-05-09'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.165.0'} />

                <p>{'FIXED: Various bugfixes and overall improvements'}</p>
                <p>{'FIXED: Desktop app crash when quitting app in MacOS'}</p>
                <p>
                    {
                        'FIXED: Marking a chat as unread sometimes marked it as read again immediately'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-05-04'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.164.1'} />
                <p>{'NEW: Improved UI of contact cards'}</p>
                <p>
                    {
                        'NEW: Call duration on active calls is now displayed on queues in the PBX tab'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-04-17'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.163.2'} />
                <p>
                    {
                        'FIXED: Ability to order eSIM not showing in some cases where it should'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-04-12'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.163.1'} />
                <p>
                    {
                        'NEW: Added keyboard shortcuts for navigation and transfering calls in the colleagues tab'
                    }
                </p>
                <p>{'NEW: Freshened up the mobile simcard settings page'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-03-28'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.162.0'} />
                <p>{'FIXED: Various bugfixes and overall improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-03-14'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.161.0'} />
                <p>
                    {
                        'FIXED: Fixed a bug while creating a call widget where auto open fails without https in the link'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-02-27'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.160.1'} />
                <p>{'FIXED: Various bugfixes and overall improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-02-21'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.159.2'} />
                <p>{'FIXED: Quicker queue status updates'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-02-16'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.159.1'} />
                <p>{'FIXED: Updated list of conference numbers'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-02-14'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.159.0'} />
                <p>
                    {
                        'FIXED: Disabled the button to pick a queue call if the user does not have call waiting enabled'
                    }
                </p>
                <p>
                    {
                        'FIXED: Queue profiles have better visual responsiveness when being changed'
                    }
                </p>
                <p>
                    {'FIXED: Unarchive button not working for archived chats'}
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-02-03'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.158.1'} />
                <p>{'FIXED: Issue with the app updating process'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-01-31'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.158.0'} />
                <p>{'FIXED: Updated Norwegian Language support'}</p>
                <p>{'FIXED: Various bugfixes and overall improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-01-24'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.157.1'} />
                <p>{'FIXED: Notification behaviour improved'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2023-01-09'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.157.0'} />
                <p>{'FIXED: Open call widgets on answer was not working'}</p>
                <p>{'FIXED: Various bugfixes and overall improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-12-06'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.156.0'} />
                <p>
                    {
                        'NEW: Added keyboard shortcut for muting mic while in call'
                    }
                </p>
                <p>
                    {
                        'FIXED: Progress tones could sometimes keep playing after other party answers'
                    }
                </p>
                <p>
                    {
                        'FIXED: Better ability to view calendar events far away in the future'
                    }
                </p>
                <p>{'FIXED: Unmute functionality with Jabra headsets'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-11-24'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.155.0'} />
                <p>
                    {
                        'NEW: Hovering over time stamps in the chat will show the full date and time'
                    }
                </p>
                <p>
                    {
                        "FIXED: Moved settings for 'call waiting' to incoming call settings -> if busy settings"
                    }
                </p>
                <p>{'FIXED: Graphical UI in the settings for wallboards'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-11-17'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.154.3'} />
                <p>{'NEW: Language support for Portuguese and Dutch'}</p>
                <p>{'NEW: Kuando Busy Light update'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-10-25'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.153.0'} />
                <p>{"FIXED: Screensharer's video isn't shown"}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-10-11'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.152.1'} />
                <p>
                    {
                        'FIXED: Video conference could sometimes enter a state where it would look like a phone call'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-09-29'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.151.1'} />
                <p>
                    {
                        'FIXED: Crash when reading message from newly created chat room'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-09-14'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.150.0'} />
                <p>{'NEW: Aggregated data for queues in My Overview'}</p>
                <p>
                    {
                        'NEW: Ability to retry sending chat messages in case of failure'
                    }
                </p>
                <p>
                    {
                        'NEW: Tooltips with keyboard shortcuts for video meeting buttons'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-09-06'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.149.1'} />
                <p>
                    {'NEW: Possibility to test audio input and output settings'}
                </p>
                <p>
                    {
                        'FIXED: First attempt at joining video meeting would sometimes fail'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-08-17'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.148.0'} />
                <p>
                    {
                        'NEW: Improved ability to toggle calls between app, mobile or other phone'
                    }
                </p>
                <p>{'FIXED: Stability improvements for video meetings'}</p>
                <p>{'FIXED: Various bugfixes'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-07-05'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.147.0'} />
                <p>
                    {
                        'NEW: Images for conference calls in the history tab are now added'
                    }
                </p>
                <p>
                    {
                        'FIXED: Screen sharing a specific window in the video conference is now possible in the electron client as well'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-06-22'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.146.0'} />
                <p>{'FIXED: Various bugfixes and overall improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-06-13'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.145.1'} />
                <p>
                    {
                        'NEW: Added setting for redirects on incoming calls when phone/softphone can not be reached'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-05-24'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.144.0'} />
                <p>{'FIXED: App could sometimes prevent computer sleep'}</p>
                <p>
                    {
                        'FIXED: Stability improvements and polish of softphone and meetings'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-05-11'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.143.1'} />
                <p>
                    {
                        'NEW: Added additional context menu on contacts when using a Contact Sync app'
                    }
                </p>
                <p>
                    {
                        'FIXED: Added warning when your voicemail inbox is about to get full'
                    }
                </p>
                <p>
                    {
                        'FIXED: Mute/unmute using some Jabra headset models did not always work'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-04-26'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.142.0'} />
                <p>
                    {
                        'FIXED: When typing in certain chats the cursor symbol could sometimes jump back to the start of the message, making the wrong part of the message being edited. Now fixed.'
                    }
                </p>
                <p>{'FIXED: Various bugfixes and overall improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-04-12'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.141.0'} />
                <p>
                    {
                        'NEW: Live badge in the chat list indicating members in a video meeting connected to a chat room'
                    }
                </p>
                <p>{'NEW: Improved screen share view for video meetings'}</p>
                <p>
                    {
                        'NEW: Ability for app users to re-join with video in merged calls'
                    }
                </p>
                <p>{'NEW: Chat sessions can be marked as unread'}</p>
                <p>
                    {
                        'NEW: All messages in a chat session can be marked as read from the right click menu'
                    }
                </p>
                <p>{'NEW: Call history for a selected number in a queue'}</p>
                <p>
                    {
                        'FIXED: Better device linking for meeting microphone and speakers'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-03-29'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.140.0'} />
                <p>{'FIXED: Better search results in call history'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-03-17'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.139.2'} />
                <p>{'NEW: Added help text when setting status message'}</p>
                <p>{'NEW: Ability to change order in team dashboard'}</p>
                <p>
                    {
                        'FIXED: Profiles are now sorted in same order as mobile apps'
                    }
                </p>
                <p>
                    {
                        'FIXED: The ability for admins to add colleagues has been removed. Use admin portal for this instead'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-03-01'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.138.0'} />
                <p>
                    {
                        'FIXED: Issue where Windows setting "Communication - mute all other sounds when in call" always muted other sounds if app was running'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-02-15'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.137.0'} />
                <p>
                    {
                        'NEW: Made it possible to get a notification when user ends a Teams call'
                    }
                </p>
                <p>
                    {
                        'FIXED: Chat input field overlapping mentions selector for long messages'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-02-03'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.136.1'} />
                <p>{'NEW: Links in your contact card are now clickable'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-01-21'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.135.3'} />
                <p>{'NEW: Changed look and feel of settings page'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2022-01-12'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.135.2'} />
                <p>
                    {
                        'NEW: Possible to start a new call while having an ongoing call'
                    }
                </p>
                <p>
                    {'NEW: Possible to merge two calls into a conference call'}
                </p>
                <p>
                    {
                        'NEW: Support for historical data in the dashboards, maximum 24 hours back'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-12-16'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.134.2'} />
                <p>
                    {
                        'FIXED: Bug when searching for voicemails and getting no hits'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-12-14'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.134.1'} />
                <p>
                    {
                        'NEW: Custom team dashboards have support for up to 18 agents'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-11-24'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.133.1'} />
                <p>{'NEW: Ability to make call via the right-click menu'}</p>
                <p>{'NEW: Added support for EPOS headsets and busylights'}</p>
                <p>
                    {
                        'FIXED: A problem where the transfer UI sometimes lingered between calls'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-11-10'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.132.0'} />
                <p>
                    {
                        'FIXED: Users who are made admin in chats will instantly get the rights without having to restart the app'
                    }
                </p>
                <p>
                    {'FIXED: Chat room names are now limited to 32 characters'}
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-10-27'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.131.1'} />
                <p>{'FIXED: Problem with call transfer'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-10-26'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.131.0'} />
                <p>
                    {'NEW: Possible to mark shared voice messages as handled'}
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-10-20'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.130.1'} />
                <p>{'FIXED: Resolved connection issues to servers'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-10-12'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.130.0'} />
                <p>{'NEW: Mark personal missed calls as handled'}</p>
                <p>{'NEW: Various chat polishes'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-10-07'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.129.2'} />
                <p>{'FIXED: Problems installing the MSI'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-09-29'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.129.0'} />
                <p>{'FIXED: Some video conference bugs'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-09-28'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.128.0'} />
                <p>
                    {'NEW: Right click menu on contacts in call history list'}
                </p>
                <p>
                    {
                        'NEW: New UI on colleague not available banner in the chat'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-09-20'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.127.2'} />
                <p>
                    {
                        'FIXED: Call widgets did not show up after adding new ones'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-09-16'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.127.1'} />
                <p>
                    {
                        'FIXED: Image preview in the slide show will now close if the image or the gray area around is clicked'
                    }
                </p>
                <p>
                    {
                        'FIXED: Bug where the conference header in right pane mode would overlap the image preview'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-09-15'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.127.0'} />
                <p>{'NEW: Support for Kuando Busylight'}</p>
                <p>
                    {
                        'NEW: Right-click menus in contacts, messages and PBX tabs'
                    }
                </p>
                <p>
                    {
                        'NEW: Added possibility to navigate between images in chat image preview'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-09-01'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.126.0'} />
                <p>
                    {
                        'NEW: Call widgets displayed on outgoing calls as well as incoming calls'
                    }
                </p>
                <p>
                    {
                        'FIXED: In some cases call widgets did not auto open even though auto open option was set'
                    }
                </p>
                <p>{'FIXED: Various bugs'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-08-20'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.125.0'} />
                <p>
                    {
                        'NEW: Added setting for handling active video conference as being in call'
                    }
                </p>
                <p>{'NEW: Public chat rooms are now searchable'}</p>
                <p>
                    {
                        'FIXED: Department of contact is now shown for minimized call'
                    }
                </p>
                <p>{'FIXED: Downlading images sent via chat now works'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-07-12'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.124.1'} />
                <p>
                    {
                        'FIXED: Bug that caused removed queues to remain in PBX list'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-07-08'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.124.0'} />
                <p>
                    {
                        'NEW: Delayed hunt groups can now be configured in the webapp'
                    }
                </p>
                <p>
                    {
                        'NEW: Possible to enable your colleagues to co-listen in on your calls'
                    }
                </p>
                <p>{'NEW: Incoming calls can be delayed to your landline'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-06-23'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.123.0'} />
                <p>{'FIXED: Various bugs resolved'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-06-09'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.122.2'} />
                <p>
                    {
                        'NEW: Complete attended call transfers from the call modal'
                    }
                </p>
                <p>
                    {
                        'NEW: Added possibility to mark missed queue calls as handled'
                    }
                </p>
                <p>
                    {'FIXED: Problem updating pause profiles time in seconds'}
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-05-26'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.121.0'} />
                <p>
                    {
                        'NEW: Added possibility to transfer calls from the call modal'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-05-11'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.120.0'} />
                <p>{'FIXED: Bugfixes and improvement work to call modal'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-04-29'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.119.1'} />
                <p>
                    {
                        'NEW: App now available in Spanish (Castilian and Latin American)'
                    }
                </p>
                <p>
                    {
                        'FIXED: Queue and redirect info is now shown when the call modal is minimized'
                    }
                </p>
                <p>
                    {
                        "FIXED: The calls in a queue's history was sometimes sorted in the wrong order"
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-04-13'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.118.1'} />
                <p>{'NEW: Redesigned call modal'}</p>
                <p>
                    {'NEW: Call waiting - users can now accept a second call'}
                </p>
                <p>
                    {
                        'NEW: Media sent in a chat can be seen in the right pane of the chat'
                    }
                </p>
                <p>
                    {
                        "FIXED: Clicking a post author's name will open their contact card"
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-03-31'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.117.1'} />
                <p>
                    {
                        'NEW: Added shortcut to profile settings in profile picker'
                    }
                </p>
                <p>
                    {
                        'NEW: Simplified terminology by changing Queues and Hunt Groups to Queuing Systems and Channels to Live Chats'
                    }
                </p>
                <p>
                    {
                        'FIXED: Resolved an issue where you could not inactivate Google and Microsoft integrations'
                    }
                </p>
                <p>
                    {'FIXED: Jabra headset buttons now work with MacOS Big Sur'}
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-03-16'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.116.0'} />
                <p>
                    {
                        'FIXED: Various bugs resolved and minor UI improvements made'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-03-08'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.115.2'} />
                <p>{'FIXED: Bug when restarting app after upgrade'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-03-03'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.115.1'} />
                <p>
                    {
                        'FIXED: Stream bug when changing video devices in the video conference'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-03-02'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.115.0'} />
                <p>
                    {
                        'NEW: The conferences have received an update with a new lobby and user interface, with support for video.'
                    }
                </p>
                <p>{'FIXED: Improved performance when viewing the pbx tab.'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-02-17'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.114.0'} />
                <p>{'FIXED: Various bugs resolved'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2021-02-03'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.113.0'} />
                <p>
                    {
                        'NEW: Stability work for the softphone experience when calling from the application.'
                    }
                </p>
                <p>
                    {
                        'FIXED: Bug where you would sometimes not hear busy tones.'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-12-17'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.112.2'} />
                <p>
                    {
                        'FIXED: The browser tab opened when logging in with third party verification closed too soon. This is now fixed.'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-12-16'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.112.1'} />
                <p>
                    {
                        'FIXED: Third party verification using Google or Microsoft will be opened in the browser instead of a client window because of Google policy changes.'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-12-08'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.112.0'} />
                <p>
                    {
                        'NEW: Possible to enable flashing activity bar icon on notification'
                    }
                </p>
                <p>{'FIXED: Improved logging from desktop client'}</p>
                <p>{'FIXED: Bug where a call could be terminated'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-11-26'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.111.1'} />
                <p>{'FIXED: Various bugs resolved for video'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-11-24'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.111.0'} />
                <p>
                    {
                        'FIXED: Sorting of voicemessages in shared voicemail improved'
                    }
                </p>
                <p>{'FIXED: Various bugs'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-11-11'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.110.2'} />
                <p>{'FIXED: Bug in mobile settings'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-11-11'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.110.1'} />
                <p>
                    {
                        'FIXED: Conference navigation in desktop application now works again'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-11-10'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.110.0'} />
                <p>
                    {
                        'NEW: Incoming calls will be turned on by default on desktop client'
                    }
                </p>
                <p>{'NEW: Better UI for unread voicemail'}</p>
                <p>{'NEW: Possible to paste in pictures in chat'}</p>
                <p>{'FIXED: Various bugs resolved'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-10-28'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.109.1'} />
                <p>
                    {
                        'FIXED: Improved presentation of matches when searching for colleagues'
                    }
                </p>
                <p>
                    {
                        'FIXED: A bug where voice messages would sometimes not be displayed'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-10-14'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.108.0'} />
                <p>
                    {
                        'NEW: Click on a contact or number in your call history to see call history with that person'
                    }
                </p>
                <p>
                    {
                        'FIXED: Linking google accounts did not work for some users'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-09-29'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.107.0'} />
                <p>
                    {
                        'NEW: Scrolling to the bottom of the pbx call history now loads older calls'
                    }
                </p>
                <p>{'NEW: New menu in chat view for easier navigation'}</p>
                <p>{'FIXED: A few bugs related to Jabra headsets'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-09-24'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.106.2'} />
                <p>{'FIXED: Some Jabra integration bugs'}</p>
                <p>
                    {
                        'FIXED: Bug where the dial pad button in the call canvas would sometimes disappear'
                    }
                </p>
                <p>
                    {'FIXED: Heavy load issues related to the queue statistics'}
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-09-02'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.105.0'} />
                <p>{'NEW: Now possible to customize service levels for PBX'}</p>
                <p>
                    {
                        'FIXED: Issue with PBX section after returning from sleep mode'
                    }
                </p>
                <p>
                    {
                        'MISC: Due to issues with headset controls in the web app, they are now available in the desktop app only'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-08-25'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.104.0'} />
                <p>{'NEW: Updated fonts to improve readability 🚀'}</p>
                <p>
                    {
                        'FIXED: Some users chat messages were not showing up on time'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-07-07'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.103.0'} />
                <p>{'FIXED: Various bugs'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-06-16'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.102.3'} />
                <p>{'FIXED: Updated information in external search on call'}</p>
                {isTelavoxBrand(getBrandKey()) && (
                    <p>{'NEW: Updated color scheme'}</p>
                )}
            </NewsWrapper>

            <DateHeader releaseDate={'2020-06-04'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.101.1'} />
                <p>{'FIXED: Additional improvements to logging'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-06-01'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.101.0'} />
                <p>
                    {
                        'FIXED: Performance improvements for customers with a large amount of queues'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-05-18'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.100.2'} />
                <p>{'FIXED: Improved logging in desktop app'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-05-13'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.100.1'} />
                <p>
                    {
                        'FIXED: Removed faulty error notification when opening settings tab'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-05-12'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.100.0'} />
                <p>{'NEW: Dashboard for monitoring queue health'}</p>
                <p>{'NEW: Setting to autostart app when computer restarts'}</p>
                <p>
                    {
                        'FIXED: Jabra base sometimes keeps ringing when call is answered'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-04-28'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.99.0'} />
                <p>
                    {
                        'NEW: The call history is now updated as soon as a call is finished'
                    }
                </p>
                <p>
                    {'NEW: It is now possible to remove your profile picture'}
                </p>
                <p>
                    {
                        'FIXED: When editing a user profile you can no longer enter more characters than are allowed'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-04-16'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.98.1'} />
                <p>
                    {
                        'FIXED: List of waiting queue calls could show duplicates in some cases'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-04-15'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.98.0'} />
                <p>
                    {
                        "NEW: It's now possible to view profile pictures by clicking them"
                    }
                </p>
                <p>
                    {
                        'NEW: External search now also shows after answering the call'
                    }
                </p>
                <p>
                    {
                        'FIXED: The application reconnects faster if it has lost connection'
                    }
                </p>
                <p>
                    {
                        "FIXED: The call modal won't get stuck in rare cases anymore"
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-03-31'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.97.0'} />
                <p>
                    {
                        'NEW: Some successful actions, such as creation and deletion of contacts, are now acknowledged in the form of a popup.'
                    }
                </p>
                <p>{"NEW: It's now possible to transfer calls on hold"}</p>
                <p>
                    {
                        'NEW: Phone numbers are now displayed in the queue call history'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-03-17'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.96.0'} />
                <p>
                    {
                        'FIXED: Updated Jabra dependency, with intentions of fixing CPU issues that some Jabra headset users has experienced'
                    }
                </p>
                <p>
                    {
                        'FIXED: Some users with newer versions of Chrome could not log in through the web'
                    }
                </p>
                <p>
                    {
                        'FIXED: Bug that inhibited users from setting a status message in some cases'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-03-06'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.95.3'} />
                <p>
                    {'FIXED: Microphone permission behaviour on Flow for Mac'}
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-03-05'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.95.2'} />
                <p>
                    {
                        'FIXED: Bug where queue agent logged in and out did not update correctly.'
                    }
                </p>
            </NewsWrapper>
            <NewsWrapper>
                <VersionHeader versionId={'1.95.1'} />
                <p>{'FIXED: Bug related to internal logging was squashed.'}</p>
            </NewsWrapper>
            <NewsWrapper>
                <VersionHeader versionId={'1.95.0'} />
                <p>
                    {
                        'FIXED: Run URL during phone calls now accepts generic protocols.'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-03-03'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.94.0'} />
                <p>
                    {
                        'FIXED: Bug causing the duration of the active profile to be reset to its default length when setting a status text.'
                    }
                </p>
                <p>
                    {
                        'FIXED: Bug that in rare cases caused the names of queue members to be displayed as "Unknown".'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-02-19'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.93.0'} />
                <p>{'NEW: Archive chats'}</p>
                <p>{'FIXED: Improved stability when parking calls'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-02-05'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.92.0'} />
                <p>
                    {
                        'NEW: Improved number formatting when displaying active queue calls.'
                    }
                </p>
                <p>
                    {
                        'FIXED: Flickering in call modal when answering queue calls.'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-01-27'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.91.2'} />
                <p>{'FIXED: Bug hiding conference item in pbx tab'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-01-24'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.91.1'} />
                <p>{'FIXED: Bug when searching in pbx tab'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2020-01-21'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.91.0'} />
                <p>{'NEW: Improved login behaviour'}</p>
                <p>{'NEW: Open external URLs on calls'}</p>
                <p>{'FIXED: Errors when picking calls from queues'}</p>
                <p>{'FIXED: Translation errors'}</p>
                <p>{'FIXED: Various bugfixes and stability improvements'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2019-12-11'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.90.0'} />
                <p>
                    {
                        'NEW: Possible to filter records by phone number in the call list'
                    }
                </p>
                <p>{'FIXED: Performance fixes in call list'}</p>
            </NewsWrapper>

            <DateHeader releaseDate={'2019-11-26'} />
            <NewsWrapper>
                <VersionHeader versionId={'1.89.0'} />
                <p>
                    {
                        'NEW: The section displaying information about ongoing queue calls now also contains the name of the agent having the call'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate="2019-11-12" />
            <NewsWrapper>
                <VersionHeader versionId="1.88.0" />
                <p>
                    {
                        'FIXED: Transferring calls to colleagues with busy status in the Colleagues view.'
                    }
                </p>
                <p>
                    {
                        'FIXED: Call controller will be presented in a correct way when a call from a queue is ringing for a longer period.'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate="2019-10-30" />
            <NewsWrapper>
                <VersionHeader versionId="1.87.0" />
                <p>
                    {
                        'FIXED: Notifications for last person logging out of queue were not displayed despite having that notification setting toggled.'
                    }
                </p>
                <p>
                    {
                        'FIXED: Updated the number presentation in the call list for Queues, to avoid redirected Suffix calls to be presented as Swedish.'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate="2019-10-16" />
            <NewsWrapper>
                <VersionHeader versionId="1.86.0" />
                <p>
                    {
                        'NEW: Call controller informs when numbers are busy, unavailable or not found'
                    }
                </p>
                <p>
                    {
                        "NEW: The call controllers size persists between calls in an ongoing state. For incoming calls, it's always large."
                    }
                </p>
                <p>
                    {
                        'NEW: Telephony and Audio settings moved to its own settings tab'
                    }
                </p>
                <p>
                    {
                        'FIXED: Issues where joining/leaving chats resulted in ghost items in the chat list'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate="2019-10-01" />
            <NewsWrapper>
                <VersionHeader versionId="1.85.0" />
                <p>
                    {
                        'NEW: Moved sim card information to the mobile page in settings'
                    }
                </p>
                <p>
                    {
                        'NEW: The call history now displays a company name if there is lookup information on that number'
                    }
                </p>
                <p>
                    {
                        'FIXED: Solved an issue with calls and voice messages not having the same end date'
                    }
                </p>
                <p>
                    {
                        'FIXED: Issues with parking a call multiple time are now fixed'
                    }
                </p>
                <p>
                    {
                        'Various bug fixes, interface optimizations and maintenance work.'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate="2019-09-18" />
            <NewsWrapper>
                <VersionHeader versionId="1.84.2" />
                <p>
                    {
                        'FIXED: Some users softphones failed to reconnect after server reconfiguration'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate="2019-09-17" />
            <NewsWrapper>
                <VersionHeader versionId="1.84.1" />
                <p>
                    {
                        'FIXED: Contacts in contacts tab sometimes collapsed on each other'
                    }
                </p>
            </NewsWrapper>
            <NewsWrapper>
                <VersionHeader versionId="1.84.0" />
                <p>
                    {
                        "NEW: Better overview of mobile data and roaming usage under 'Mobile settings'"
                    }
                </p>
                <p>{'NEW: Improved snappiness of contacts tab'}</p>
                <p>
                    {
                        'FIXED: New messages indicator would sometimes show up in multiple places'
                    }
                </p>
                <p>
                    {
                        'FIXED: Shared voicemails were missing number lookup information'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate="2019-09-04" />
            <NewsWrapper>
                <VersionHeader versionId="1.83.1" />
                <p>
                    {
                        'FIXED: A bug caused phones to sometimes not detect incoming calls.'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate="2019-09-02" />
            <NewsWrapper>
                <VersionHeader versionId="1.83.0" />
                <p>
                    {
                        'NEW: New menu in call canvas with option "Toggle call to other device" and shortcuts to the colleague and PBX tabs, making it easier to transfer your call.'
                    }
                </p>
                <p>
                    {
                        'FIXED: Resolved issues with creating chats and chat rooms. As a bonus we also updated the look and feel.'
                    }
                </p>
                <p>{'Optimization of the chat list to improve performance.'}</p>
            </NewsWrapper>

            <DateHeader releaseDate="2019-08-23" />
            <NewsWrapper>
                <VersionHeader versionId="1.82.0" />
                <p>
                    {'NEW: Pause GIFs'}
                    <br />
                    {'GIFs are now pausable in chats'}
                </p>
                <p>{'NEW: Admin can change colleagues operator notes'}</p>
                <p>
                    {'NEW: UX improvements'}
                    <br />
                    {'Sticky headers in colleagues and pbx lists'}
                </p>
                <p>{'Various bug fixes'}</p>
            </NewsWrapper>

            <DateHeader releaseDate="2019-07-23" />
            <NewsWrapper>
                <VersionHeader versionId="1.81.2" />
                <p>
                    {'NEW: Improved queue call history'}
                    <br />
                    {
                        'As a queue agent, you can now see how long a caller has waited for, the duration of the call and which agent answered it.'
                    }
                </p>
                <p>
                    {'NEW: Sticky headers'}
                    <br />
                    {
                        'If you run Flow for Windows/Mac or Flow in Chrome, Firefox or Safari you may notice that section headers now follow along when scrolling long lists. This mainly affects the call history, making it easier to see when a call was made.'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate="2019-07-09" />
            <NewsWrapper>
                <VersionHeader versionId="1.80.0" />
                <p>
                    {
                        'NEW: Release notes now available! Here we will keep you updated with the latest additions and changes in the Flow client.'
                    }
                </p>
            </NewsWrapper>

            <DateHeader releaseDate="2019-06-25" />
            <NewsWrapper>
                <VersionHeader versionId="1.79.0" />
                <p>
                    {
                        'NEW: Auto Answer has been implemented for both out- and incoming calls. To activate you will find the feature in settings/general/call method'
                    }
                </p>
                <p>
                    {
                        'NEW: The call modal has been updated with a new look. It will now be placed in the lower left corner'
                    }
                </p>
                <p>
                    {'NEW: The sign in page has been updated with a new look.'}
                </p>
                <p>
                    {
                        'NEW: Pick call from colleague. When a colleague is receiving an incoming call on her fixed number, you can now pick that call'
                    }
                </p>
            </NewsWrapper>
        </Page>
    );
};

export default ReleaseNotes;
